/**
 * Cache Settings API Service
 * Provides methods for managing cache configuration settings
 */

import type { CacheSettings, CacheSettingsUpdateRequest } from '../types/cacheSettings';
import { ApiService } from './api';

/**
 * Convert camelCase to snake_case for API compatibility
 */
function toSnakeCase(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map((item) => toSnakeCase(item));
  if (typeof obj !== 'object') return obj;

  const snakeObj: Record<string, unknown> = {};
  for (const key in obj) {
    const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
    snakeObj[snakeKey] = toSnakeCase((obj as Record<string, unknown>)[key]);
  }
  return snakeObj;
}

/**
 * Convert snake_case to camelCase for frontend compatibility
 */
function toCamelCase(obj: unknown): unknown {
  if (obj === null || obj === undefined) return obj;
  if (Array.isArray(obj)) return obj.map((item) => toCamelCase(item));
  if (typeof obj !== 'object') return obj;

  const camelObj: Record<string, unknown> = {};
  for (const key in obj) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    camelObj[camelKey] = toCamelCase((obj as Record<string, unknown>)[key]);
  }
  return camelObj;
}

/**
 * Cache Settings API Service
 * Uses ApiService for authenticated requests
 */
export const cacheSettingsApi = {
  /**
   * Get current cache settings
   */
  async getCacheSettings(): Promise<CacheSettings> {
    const data = await ApiService.request<Record<string, unknown>>('/api/admin/settings/cache');
    return toCamelCase(data) as CacheSettings;
  },

  /**
   * Update cache settings (partial updates supported)
   */
  async updateCacheSettings(request: CacheSettingsUpdateRequest): Promise<CacheSettings> {
    const snakeCaseRequest = toSnakeCase(request);

    const data = await ApiService.request<Record<string, unknown>>('/api/admin/settings/cache', {
      method: 'PUT',
      body: JSON.stringify(snakeCaseRequest),
    });

    return toCamelCase(data) as CacheSettings;
  },
};

// Also export as class for backwards compatibility with tests
export class CacheSettingsApi {
  async getCacheSettings(): Promise<CacheSettings> {
    return cacheSettingsApi.getCacheSettings();
  }

  async updateCacheSettings(request: CacheSettingsUpdateRequest): Promise<CacheSettings> {
    return cacheSettingsApi.updateCacheSettings(request);
  }
}

export default cacheSettingsApi;
